// eslint-disable-next-line import/no-unresolved
import { useIsAuthenticated } from '@creditsights/navbar-web';
import { DataLoadError, Loader, useCopyPasteGuard } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { createBrowserHistory } from 'history';
import React, { useEffect } from 'react';
import { osName } from 'react-device-detect';
import { useQueryClient } from 'react-query';
import { Navigate, Route, Routes } from 'react-router-dom';
import { COMPANY_ROUTES, EXTERNAL_ROUTES, MY_CONTENT_ROUTES, ROUTES } from './Base.constants';
import styles from './Base.module.scss';
import BrowserHistoryRouter from './components/BrowserHistoryRouter/BrowserHistoryRouter';
import { DEFAULT_SUBSCRIPTIONS_EMAIL_ADDRESS } from './components/ContactInformation/ContactInformation.constants';
import GlobalModal from './components/GlobalModal';
import NoPermissions from './components/NoPermissions/NoPermissions';
import ScrollToTop from './components/ScrollToTop';
import UpgradeToPrint from './components/UpgradeToPrint';
import { IssuersFilterContextProvider } from './hooks/filters/useIssuersFilterContext';
import useFetchUserPermissions from './hooks/permissions/useFetchUserPermissions';
import useFetchUserProfile from './hooks/profile/useFetchUserProfile';
import { useFetchRecentlyViewed } from './hooks/recentlyViewed';
import useFetchFilters from './hooks/tags/useFetchFilters';
import useMutationErrorToast from './hooks/toast/useMutationErrorToast';
import useGetCustomHistory from './hooks/useGetCustomHistory';
import useInitBroadcastChannels from './hooks/useInitBroadcastChannels';
import { IssuerType } from './models';
import Article from './pages/Article';
import ArticleOutletWrapper from './pages/Article/ArticleOutletWrapper';
import Companies from './pages/Companies';
import Company from './pages/Company';
import CompanyDealsTab from './pages/Company/CompanyDealsTab';
import CompanyDocketsTab from './pages/Company/CompanyDocketsTab';
import CompanyDocumentsTab from './pages/Company/CompanyDocumentsTab';
import CompanyBondsTable from './pages/Company/CompanyDocumentsTab/CompanyBondsTable';
import CompanyFilingsTable from './pages/Company/CompanyDocumentsTab/CompanyFilingsTable';
import CompanyLoansTable from './pages/Company/CompanyDocumentsTab/CompanyLoansTable';
import CompanyFinancialsTab from './pages/Company/CompanyFinancialsTab';
import CompanyRecentlyPublishedTab from './pages/Company/CompanyRecentlyPublishedTab/CompanyRecentlyPublishedTab';
import CompanyRiskTab from './pages/Company/CompanyRiskTab/CompanyRiskTab';
import CustomPage from './pages/CustomPage';
import Dashboards from './pages/Dashboards';
import Dashboard from './pages/Dashboards/Dashboard';
import Download from './pages/Download';
import Feed from './pages/Feeds';
import GenieStandalone from './pages/GenieStandalone';
import Login from './pages/Login';
import Logout from './pages/Logout/Logout';
import Markets from './pages/Markets';
import MyContent from './pages/MyContent';
import ArticlesTab from './pages/MyContent/ArticlesTab/ArticlesTab';
import CovenantsTab from './pages/MyContent/CovenantsTab/CovenantsTab';
import EmailPreferences from './pages/MyContent/EmailPreferenceTab/EmailPreferences';
import ManagePreferencesTab from './pages/MyContent/ManagePreferencesTab/ManagePreferencesTab';
import SearchesTab from './pages/MyContent/SearchesTab/SearchesTab';
import Watchlist from './pages/MyContent/WatchlistsTab/Watchlist/Watchlist';
import WatchlistsTab from './pages/MyContent/WatchlistsTab/WatchlistsTab';
import NotFound from './pages/NotFound';
import Redirect from './pages/Redirect/Redirect';
import SearchResults from './pages/SearchResults';
import Unauthorized from './pages/Unauthorized';
import { ApiError } from './services/api-error.model';
import { isForbidden, isNotFound } from './utils/navigationUtils';
import { hasPermissionToCopyPaste, hasPermissionToPrint, hasUIPermission } from './utils/permissionsUtils';

const cx = classNames.bind(styles);

const QUERY_RETRY_COUNT = 3;

function shouldRetry(failureCount: number, error): boolean {
  return !isNotFound(error) && !isForbidden(error) && failureCount < QUERY_RETRY_COUNT;
}

const Base = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useIsAuthenticated(queryClient);
  const addOSAttribute = () => document.documentElement.setAttribute('data-os', osName);
  const { showErrorToast } = useMutationErrorToast();

  // it has to be defined before custom fetch hooks in order to set default options for queryClient
  queryClient.setDefaultOptions({
    queries: {
      staleTime: 15 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: shouldRetry,
    },
    mutations: {
      onError: (error) => {
        showErrorToast(error as ApiError);
      },
    },
  });

  const history = useGetCustomHistory(createBrowserHistory());
  const { isError: isFilterError } = useFetchFilters([], isAuthenticated);
  const {
    isLoading: isPermissionsLoading,
    isError: isPermissionsError,
    data: userPermissions,
    isFetched: isPermissionsFetched,
  } = useFetchUserPermissions(isAuthenticated);
  useCopyPasteGuard(hasPermissionToCopyPaste(userPermissions));

  // need to fetch recently viewed articles in root as these articles needs to be accessed in many places for mutations of recently viewed to work
  useFetchRecentlyViewed(isAuthenticated);
  const {
    data: profile,
    isLoading: isProfileLoading,
    isFetched: isProfileFetched,
  } = useFetchUserProfile(isAuthenticated);
  const salesPersonEmail = profile?.assignedAccountManager?.email || DEFAULT_SUBSCRIPTIONS_EMAIL_ADDRESS;
  const showNoPermissionsModal = isPermissionsFetched && !isPermissionsLoading && !hasUIPermission(userPermissions);
  const isProfileActive = isProfileFetched && !isProfileLoading && profile?.active;

  useEffect(() => {
    addOSAttribute();
  }, []);

  useInitBroadcastChannels();

  if (isFilterError || isPermissionsError) {
    const handleRetry = () => {
      window.location.reload();
    };

    return (
      <BrowserHistoryRouter history={history}>
        {showNoPermissionsModal && <NoPermissions salesPersonEmail={salesPersonEmail} />}
        <DataLoadError disabled={showNoPermissionsModal} onRetryClick={handleRetry} />
        <Routes>
          <Route path={ROUTES.logout} element={<Logout />} />
        </Routes>
      </BrowserHistoryRouter>
    );
  }

  if (isAuthenticated && (isPermissionsLoading || isProfileLoading)) {
    return (
      <BrowserHistoryRouter history={history}>
        <div className={cx('loader-container')}>
          <Loader text={null} />
        </div>
        <Routes>
          <Route path={ROUTES.logout} element={<Logout />} />
        </Routes>
      </BrowserHistoryRouter>
    );
  }

  if (isAuthenticated && isProfileActive === false) {
    return (
      <BrowserHistoryRouter history={history}>
        <NoPermissions salesPersonEmail={salesPersonEmail} />
        <Routes>
          <Route path={ROUTES.logout} element={<Logout />} />
        </Routes>
      </BrowserHistoryRouter>
    );
  }

  const canPrint = hasPermissionToPrint(userPermissions);
  if (isAuthenticated) {
    return (
      <BrowserHistoryRouter history={history}>
        <ScrollToTop />
        {!canPrint && <UpgradeToPrint />}
        <IssuersFilterContextProvider>
          <div className={cx('main-content', { 'no-print': !canPrint })} data-testid="main-content">
            <Routes>
              <Route path="">
                <Route path={ROUTES.base} element={<Navigate to={ROUTES.feed} />} />
                <Route path={ROUTES.login} element={<Login />} />
                <Route path={ROUTES.genieStandalone} element={<GenieStandalone />} />
                <Route path={`${ROUTES.feed}`} element={<Feed />} />
                <Route path={`${ROUTES.globalFeed}`} element={<Feed />} />
                <Route path={ROUTES.specialSituations} element={<Feed key="specialSituations" />} />
                <Route path={ROUTES.dashboards()} element={<Dashboards />}>
                  <Route path={ROUTES.dashboards(':watchListId', true)} element={<Dashboard />} />
                </Route>
                <Route path={`${ROUTES.markets}`} element={<Markets />} />
                <Route path={ROUTES.customPage(':key')} element={<CustomPage />} />
                <Route path={`${ROUTES.search}/*`} element={<SearchResults />} />
                <Route path={ROUTES.article(':articleId')} element={<Article />} />
                <Route path={ROUTES.notFound} element={<NotFound />} />
                <Route path={ROUTES.redirect} element={<Redirect />} />
                <Route path={ROUTES.logout} element={<Logout />} />
                <Route path={ROUTES.municipal(':issuerId')} element={<Company issuerType={IssuerType.Municipal} />}>
                  <Route path="" element={<Navigate to={COMPANY_ROUTES.recentlyPublished} replace />} />
                  <Route path={COMPANY_ROUTES.recentlyPublished} element={<CompanyRecentlyPublishedTab />} />
                </Route>
                <Route path={ROUTES.companies()} element={<Companies />} />
                <Route path={ROUTES.company()}>
                  <Route path={ROUTES.company(':issuerId', true)} element={<Company issuerType={IssuerType.Company} />}>
                    <Route path={COMPANY_ROUTES.recentlyPublished} element={<CompanyRecentlyPublishedTab />} />
                    <Route path={COMPANY_ROUTES.risk} element={<CompanyRiskTab />} />
                    <Route path={COMPANY_ROUTES.deals}>
                      <Route path="" element={<Navigate to="bonds" replace />} />
                      <Route path={COMPANY_ROUTES.loans(':dealType', true)} element={<CompanyDealsTab />} />
                      <Route path={COMPANY_ROUTES.bonds(':dealType', true)} element={<CompanyDealsTab />} />
                    </Route>
                    <Route path={COMPANY_ROUTES.documents} element={<CompanyDocumentsTab />}>
                      <Route path={COMPANY_ROUTES.loansDocuments} element={<CompanyLoansTable />} />
                      <Route path={COMPANY_ROUTES.bondsDocuments} element={<CompanyBondsTable />} />
                      <Route path={COMPANY_ROUTES.filingsDocuments} element={<CompanyFilingsTable />} />
                    </Route>
                    <Route path={COMPANY_ROUTES.dockets} element={<CompanyDocketsTab />} />
                    <Route path={COMPANY_ROUTES.financials} element={<CompanyFinancialsTab />} />
                  </Route>
                </Route>
                <Route
                  path={EXTERNAL_ROUTES.externalEmailPrefrences}
                  element={<Navigate to={`${ROUTES.myContent}/${MY_CONTENT_ROUTES.internalEmailPrefrences}`} replace />}
                />
                <Route path={ROUTES.myContent} element={<MyContent />}>
                  <Route path={MY_CONTENT_ROUTES.articles()} element={<ArticlesTab />}>
                    <Route path={MY_CONTENT_ROUTES.articles(':articleId', true)} element={<ArticleOutletWrapper />} />
                  </Route>
                  <Route path={MY_CONTENT_ROUTES.internalEmailPrefrences} element={<EmailPreferences />} />
                  <Route path={MY_CONTENT_ROUTES.covenants} element={<CovenantsTab />} />
                  <Route path={MY_CONTENT_ROUTES.managePreferences} element={<ManagePreferencesTab />} />
                  <Route path={MY_CONTENT_ROUTES.searches} element={<SearchesTab />} />
                  <Route path={MY_CONTENT_ROUTES.watchlists()} element={<WatchlistsTab />}>
                    <Route path={MY_CONTENT_ROUTES.watchlists(':id', true)} element={<Watchlist />} />
                  </Route>
                </Route>
              </Route>
              <Route path={ROUTES.download} element={<Download />} />
              <Route path="*" element={<Navigate to={ROUTES.notFound} replace />} />
            </Routes>
          </div>
          <GlobalModal />
        </IssuersFilterContextProvider>
      </BrowserHistoryRouter>
    );
  }
  return (
    <BrowserHistoryRouter history={history}>
      <Routes>
        <Route path={ROUTES.unauthorized} element={<Unauthorized />} />
        <Route path="*" element={<Login />} />
      </Routes>
    </BrowserHistoryRouter>
  );
};

export default Base;
